import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import type {
  SupportedCurrency,
  TokenPaymentMethod,
} from '@sorare/core/src/__generated__/globalTypes';
import { Currency, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { GemIcon } from '@sorare/core/src/atoms/icons/GemIcon';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BrandL,
  BrandS,
  BrandXXL,
  HeadlineXL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Img } from '@sorare/core/src/atoms/ui/Img';
import {
  IOsAppFeature,
  NotIOsAppFeature,
} from '@sorare/core/src/components/BuyableFeature';
import { Dialog } from '@sorare/core/src/components/dialog';
import { useAmountWithConversion } from '@sorare/core/src/hooks/useAmountWithConversion';
import { useIsMobileApp } from '@sorare/core/src/hooks/useIsMobileApp';
import { useMonetaryAmount } from '@sorare/core/src/hooks/useMonetaryAmount';
import { useEvents } from '@sorare/core/src/lib/events/useEvents';
import { glossary, payment } from '@sorare/core/src/lib/glossary';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { thinMaterial } from '@sorare/core/src/style/utils';

import gemsPackUrl from '#src/assets/gems/gems.webp';
import { LazyPaymentProvider } from '#src/components/buyActions/LazyPaymentProvider/index.tsx';
import { Attribute } from '#src/components/buyActions/PaymentBox/CardOverview/index.tsx';
import { useAcceptOffer } from '#src/hooks/offers/useAcceptOffer.ts';
import { usePollInGameCurrencyPackDelivery } from '#src/hooks/primaryBuy/usePollInGameCurrencyPackDelivery.tsx';

import type { GemsPack_inGameCurrencyPurchasableItem } from './__generated__/index.graphql.ts';
import ellipse from './assets/ellipse.svg';

const Root = styled(Vertical).attrs({ gap: 2 })<{ background: string }>`
  position: relative;
  border-radius: var(--double-unit);
  padding: var(--intermediate-unit);
  justify-content: space-between;
  text-align: center;
  ${thinMaterial}
  border: 1px solid var(--c-nd-150);

  &.useInGameCurrencyPack {
    background: no-repeat top / contain url(${props => props.background});
    background-size: contain;
    background-position: top;
    aspect-ratio: 296 / 368;
  }
`;

const Container = styled(Vertical).attrs({ center: true, gap: 2 })`
  flex-grow: 1;
  justify-content: flex-start;
`;

const backgroundCss = css`
  --square-width: var(--double-and-a-half-unit);
  background:
    linear-gradient(180deg, transparent 0%, rgba(0, 0, 0, 0.7) 100%),
    repeating-linear-gradient(
      to left,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    repeating-linear-gradient(
      to bottom,
      var(--c-nd-150) 0px,
      var(--c-nd-150) 1px,
      transparent 1px,
      transparent var(--square-width)
    ),
    linear-gradient(180deg, #284b30 0%, rgba(0, 0, 0, 0.7) 100%);
`;

const Leftcolumn = styled(Horizontal).attrs({ gap: 2, center: true })`
  padding: var(--double-unit);
  ${backgroundCss}
  img {
    max-width: 120px;
  }

  @media ${laptopAndAbove} {
    flex-direction: column;
    align-items: center;
    justify-content: center;
    --square-width: calc(var(--unit) * 5);
    img {
      max-width: none;
    }
  }
`;

const AttributeWrapper = styled(Horizontal).attrs({ gap: 0.5 })`
  border-radius: var(--double-unit);
  background-color: rgba(var(--c-rgb-white), 0.1);
`;

const Value = styled(AttributeWrapper)`
  padding: var(--half-unit) var(--unit);
  img {
    width: 24px;
  }
`;

const DialogBody = styled(Vertical).attrs({ center: true })`
  display: flex;
  flex-direction: column;
  text-align: center;
  gap: var(--double-unit);
  padding: var(--double-unit);
  min-height: 100%;
  ${backgroundCss}
  --square-width: calc(var(--unit) * 5);
`;

const PictureWrapper = styled(Vertical).attrs({ center: true })`
  position: relative;
  isolation: isolate;
`;

const Ellipse = styled(Img).attrs({ src: ellipse })`
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
`;

const Picture = styled(Img)`
  width: calc(10 * var(--unit));
  height: calc(10 * var(--unit));
  object-fit: cover;
`;

const PositionnedClose = styled(Horizontal).attrs({ center: true })`
  position: absolute;
  top: var(--double-unit);
  right: var(--double-unit);
  z-index: 1;
`;

const GemsPackPicture = styled(Img)`
  width: calc(30 * var(--unit));
  height: calc(15 * var(--unit));
  object-fit: contain;
`;

type Props = {
  gemsPack: GemsPack_inGameCurrencyPurchasableItem;
  onClose?: () => void;
};

export const GemsPack = ({ gemsPack, onClose }: Props) => {
  const { toMonetaryAmount } = useMonetaryAmount();
  const [dialogOpened, setDialogOpened] = useState(false);
  const [paymentSucceeded, setPaymentSucceeded] = useState(false);
  const acceptOffer = useAcceptOffer();
  const { postMessage, isMobileApp } = useIsMobileApp();

  const track = useEvents();

  const onPaymentSucceeded = (success: boolean) => {
    if (success) setPaymentSucceeded(true);
  };

  const { polling, startPolling } =
    usePollInGameCurrencyPackDelivery(onPaymentSucceeded);

  const {
    id: packId,
    pictureUrl,
    bonusAmount,
    baseAmount,
    price: packPrice,
  } = gemsPack;

  const paymentBoxPictureUrl = pictureUrl || gemsPackUrl;

  const price = toMonetaryAmount(packPrice);

  const buyWithWallet = async ({
    supportedCurrency,
    tokenPaymentMethod,
    applePayPaymentToken,
  }: {
    supportedCurrency: SupportedCurrency;
    tokenPaymentMethod?: TokenPaymentMethod;
    applePayPaymentToken?: ApplePayJS.ApplePayPaymentToken;
  }) => {
    const { errors, deal } = await acceptOffer({
      supportedCurrency,
      purchasableItemId: packId,
      useAvailableCredits: false,
      attemptReference: null,
      tokenPaymentMethod,
      applePayPaymentToken,
    });
    if (deal) {
      return onPaymentSucceeded(true);
    }
    return { err: errors };
  };

  const { main } = useAmountWithConversion({ monetaryAmount: price });

  const onCloseSuccessDialog = () => {
    setDialogOpened(false);
    setPaymentSucceeded(false);
    if (isMobileApp) {
      postMessage('refreshGemsBalance');
    }
    onClose?.();
  };

  if (price.isZero) return null;
  return (
    <>
      <Root background={pictureUrl || ''}>
        <Container>
          <PictureWrapper>
            <Ellipse />
            <Picture src={pictureUrl || ''} alt="" width="320" height="290" />
          </PictureWrapper>

          <Vertical center>
            <Horizontal>
              <BrandS>{baseAmount}</BrandS>
              <GemIcon size={2} />
            </Horizontal>
            {bonusAmount > 0 && (
              <Horizontal>
                <BrandS color="var(--c-green-200)">
                  <FormattedMessage
                    defaultMessage="+{amount} bonus"
                    values={{ amount: bonusAmount }}
                  />
                </BrandS>
              </Horizontal>
            )}
          </Vertical>
        </Container>
        <IOsAppFeature exceptMarketAuthorizedIosApp>
          <Button
            size="small"
            onClick={() => {
              track('Click Gem Package', {
                eurAmount: price.eurCents,
                gemAmount: baseAmount,
                purchasableItemId: packId,
              });
              postMessage('buyInGameCurrencyPack', {
                packId,
              });
            }}
          >
            {main}
          </Button>
        </IOsAppFeature>
        <NotIOsAppFeature exceptMarketAuthorizedIosApp>
          <Button
            size="small"
            onClick={() => {
              track('Click Gem Package', {
                eurAmount: price.eurCents,
                gemAmount: baseAmount,
                purchasableItemId: packId,
              });

              setDialogOpened(true);
            }}
          >
            {main}
          </Button>
        </NotIOsAppFeature>
      </Root>
      {!!dialogOpened && (
        <LazyPaymentProvider
          paymentProps={{
            canChangeRefCurrency: true,
            onSuccess: () => startPolling(),
            onSubmit: buyWithWallet,
            cta: payment.confirmAndPay,
            canUseConversionCredit: false,
            currencies: [Currency.FIAT, Currency.ETH],
            sport: Sport.FOOTBALL,
            useKlarna: false,
            objectId: packId,
            objectType: 'inGameCurrencyPack',
            price,
          }}
          paymentBoxProps={{
            loadingPolling: polling,
          }}
          paymentDialogProps={{
            onClose: () => {
              setDialogOpened(false);
            },
            title: (
              <HeadlineXL as="h5">
                <FormattedMessage {...payment.paymentBoxTitle} />
              </HeadlineXL>
            ),
            tokenPreview: (
              <Leftcolumn>
                <GemsPackPicture src={paymentBoxPictureUrl || ''} alt="" />
                <Vertical gap={0.5} center>
                  <Attribute
                    label={<FormattedMessage defaultMessage="Bundle of Gems" />}
                    value={
                      <Value>
                        +{baseAmount} <GemIcon />
                      </Value>
                    }
                  />
                  {bonusAmount > 0 && (
                    <Attribute
                      label={<FormattedMessage defaultMessage="Bonus" />}
                      value={
                        <Value>
                          +{bonusAmount} <GemIcon />
                        </Value>
                      }
                    />
                  )}
                </Vertical>
              </Leftcolumn>
            ),
          }}
        />
      )}
      {!!paymentSucceeded && (
        <Dialog
          open
          maxWidth="xs"
          fullWidth
          hideHeader
          onClose={onCloseSuccessDialog}
          footer={
            <Button
              size="medium"
              color="secondary"
              onClick={onCloseSuccessDialog}
              fullWidth
            >
              <FormattedMessage {...glossary.continue} />
            </Button>
          }
        >
          {({ CloseButton }) => (
            <DialogBody>
              <PositionnedClose>
                <CloseButton onClose={onCloseSuccessDialog} />
              </PositionnedClose>
              <GemsPackPicture
                src={paymentBoxPictureUrl || ''}
                alt=""
                width="320"
                height="290"
              />
              <BrandXXL color="var(--c-green-200)">
                {bonusAmount + baseAmount}
              </BrandXXL>
              <BrandL>
                <FormattedMessage defaultMessage="Thank you for  your purchase!" />
              </BrandL>
              <LabelM>
                <FormattedMessage defaultMessage="Use your gems in the marketplace to sign new players." />
              </LabelM>
            </DialogBody>
          )}
        </Dialog>
      )}
    </>
  );
};

GemsPack.fragments = {
  inGameCurrencyPurchasableItem: gql`
    fragment GemsPack_inGameCurrencyPurchasableItem on InGameCurrencyPurchasableItem {
      slug
      id
      baseAmount
      bonusAmount
      pictureUrl
      price {
        ...useMonetaryAmount_monetaryAmount
      }
    }
    ${useMonetaryAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<GemsPack_inGameCurrencyPurchasableItem>,
};
